/** Callback that returns the rendered width of a single character in mm. */
export type CharMeasure = (char: string) => number;

function measureString(text: string, measure: CharMeasure): number {
  let width = 0;
  for (let i = 0; i < text.length; i++) {
    width += measure(text.charAt(i));
  }
  return width;
}

/**
 * Word-wraps text to fit within `availableWidthMm` using measured character widths.
 * Words longer than the available width are force-broken with hyphens.
 */
export function wrapText(
  value: string,
  availableWidthMm: number,
  measure: CharMeasure,
): string[] {
  if (availableWidthMm <= 0) {
    return [value];
  }

  const words = value.split(/\s+/).filter((part) => part.length > 0);
  if (words.length === 0) {
    return [""];
  }

  const spaceWidth = measure(" ");
  const lines: string[] = [];
  let current = "";
  let currentWidth = 0;

  for (const word of words) {
    const wordWidth = measureString(word, measure);

    // Word exceeds entire line width — force break it.
    if (wordWidth > availableWidthMm) {
      if (current.length > 0) {
        lines.push(current);
        current = "";
        currentWidth = 0;
      }

      let chunk = "";
      let chunkWidth = 0;

      for (let i = 0; i < word.length; i++) {
        const char = word.charAt(i);
        const charWidth = measure(char);
        if (chunkWidth + charWidth > availableWidthMm && chunk.length > 0) {
          lines.push(chunk);
          chunk = "";
          chunkWidth = 0;
        }
        chunk += char;
        chunkWidth += charWidth;
      }

      if (chunk.length > 0) {
        current = chunk;
        currentWidth = chunkWidth;
      }
      continue;
    }

    if (current.length === 0) {
      current = word;
      currentWidth = wordWidth;
      continue;
    }

    if (currentWidth + spaceWidth + wordWidth <= availableWidthMm) {
      current += ` ${word}`;
      currentWidth += spaceWidth + wordWidth;
    } else {
      lines.push(current);
      current = word;
      currentWidth = wordWidth;
    }
  }

  if (current.length > 0) {
    lines.push(current);
  }

  return lines;
}

/**
 * Wraps text, respecting explicit `\&` line break separators.
 */
export function wrapTextWithExplicitBreaks(
  value: string,
  availableWidthMm: number,
  measure: CharMeasure,
): string[] {
  return value
    .split("\\&")
    .flatMap((line) => wrapText(line, availableWidthMm, measure));
}

/**
 * Fills lines character-by-character up to `availableWidthMm` per line.
 */
function chunkByWidth(
  value: string,
  availableWidthMm: number,
  measure: CharMeasure,
): string[] {
  if (value.length === 0) {
    return [""];
  }

  const chunks: string[] = [];
  let chunk = "";
  let chunkWidth = 0;

  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    const charWidth = measure(char);
    if (chunkWidth + charWidth > availableWidthMm && chunk.length > 0) {
      chunks.push(chunk);
      chunk = "";
      chunkWidth = 0;
    }
    chunk += char;
    chunkWidth += charWidth;
  }

  if (chunk.length > 0) {
    chunks.push(chunk);
  }

  return chunks;
}

export interface CharFillResult {
  lines: string[];
  overflowed: boolean;
}

/**
 * Character-fill layout: packs characters into lines by measured width.
 */
export function charFillLines(
  value: string,
  availableWidthMm: number,
  maxLines: number,
  measure: CharMeasure,
): CharFillResult {
  const safeMaxLines = Math.max(1, maxLines);
  const wrapped = value
    .split("\\&")
    .flatMap((line) => chunkByWidth(line, availableWidthMm, measure));

  if (wrapped.length <= safeMaxLines) {
    return {
      lines: wrapped.length === 0 ? [""] : wrapped,
      overflowed: false,
    };
  }

  return {
    lines: wrapped.slice(0, safeMaxLines),
    overflowed: true,
  };
}

/**
 * Character-fill with ellipsis appended to the last visible line.
 */
export function charFillWithEllipsis(
  value: string,
  availableWidthMm: number,
  maxLines: number,
  measure: CharMeasure,
): string[] {
  const base = charFillLines(value, availableWidthMm, maxLines, measure);
  if (!base.overflowed) {
    return base.lines;
  }

  const visible = [...base.lines];
  return applyEllipsisToLastLine(visible, availableWidthMm, measure);
}

/**
 * Applies ellipsis to the last line of word-wrapped text that overflows maxLines.
 */
export function ellipsizeLines(
  wrappedLines: string[],
  maxLines: number,
  availableWidthMm: number,
  measure: CharMeasure,
): string[] {
  const normalizedMaxLines = Math.max(1, maxLines);
  const visible = wrappedLines.slice(0, normalizedMaxLines);

  if (visible.length === 0) {
    return ["..."];
  }

  return applyEllipsisToLastLine(visible, availableWidthMm, measure);
}

/**
 * Trims the last line and appends "..." so the result fits within availableWidthMm.
 */
function applyEllipsisToLastLine(
  lines: string[],
  availableWidthMm: number,
  measure: CharMeasure,
): string[] {
  const ellipsis = "...";
  const ellipsisWidth = measureString(ellipsis, measure);

  // If ellipsis alone doesn't fit, use as many dots as possible.
  if (ellipsisWidth >= availableWidthMm) {
    const dotWidth = measure(".");
    const dotCount = Math.max(1, Math.floor(availableWidthMm / dotWidth));
    lines[lines.length - 1] = ".".repeat(dotCount);
    return lines;
  }

  const contentBudget = availableWidthMm - ellipsisWidth;
  const last = lines[lines.length - 1] ?? "";
  let trimmed = "";
  let width = 0;

  for (let i = 0; i < last.length; i++) {
    const char = last.charAt(i);
    const charWidth = measure(char);
    if (width + charWidth > contentBudget) {
      break;
    }
    trimmed += char;
    width += charWidth;
  }

  lines[lines.length - 1] = `${trimmed}${ellipsis}`;
  return lines;
}
